import { useEffect, useRef, useState } from "react";

const stats = [
  { value: "10+", label: "Years in international sales", suffix: "" },
  { value: "€3M+", label: "Projects managed", suffix: "" },
  { value: "€200K+", label: "Funding secured", suffix: "" },
  { value: "CEE", label: "Deep market expertise", suffix: "" },
];

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Authority() {
  const { ref, inView } = useInView();

  return (
    <section id="authority" className="bg-[#080808] py-32 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={ref} className={`text-center mb-20 transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-16 h-px bg-[#C9A84C]/40" />
            <span className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase font-light font-['Outfit']">Our Track Record</span>
            <div className="w-16 h-px bg-[#C9A84C]/40" />
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl lg:text-6xl font-light text-white">
            Built on <em className="text-[#C9A84C] not-italic">real</em> market experience
          </h2>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#C9A84C]/10">
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`bg-[#080808] p-10 lg:p-14 flex flex-col items-center text-center group hover:bg-[#0d0d0d] transition-all duration-500 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <span className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#C9A84C] group-hover:scale-105 transition-transform duration-300 inline-block">
                {stat.value}
              </span>
              <div className="w-8 h-px bg-[#C9A84C]/30 my-4 group-hover:w-16 transition-all duration-500" />
              <span className="text-white/50 text-sm font-light tracking-wide font-['Outfit'] leading-snug">
                {stat.label}
              </span>
            </div>
          ))}
        </div>

        {/* Bottom statement */}
        <div className={`mt-20 border border-[#C9A84C]/15 p-10 lg:p-14 transition-all duration-700 delay-500 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <div className="flex-1">
              <img
                src="https://public.readdy.ai/ai/img_res/b5400ef90ca82eaec2794280efe079d7.jpg"
                alt="EmCaLu AI Agency team expertise"
                title="Eastern Europe sales consultants"
                className="w-full h-64 lg:h-80 object-cover object-top"
              />
            </div>
            <div className="flex-1 space-y-6">
              <p className="font-['Cormorant_Garamond'] text-2xl lg:text-3xl font-light text-white/80 leading-relaxed italic">
                &ldquo;We don&apos;t just consult. We become your dedicated sales force in Eastern European markets — with the AI infrastructure to scale results.&rdquo;
              </p>
              <div className="w-12 h-px bg-[#C9A84C]" />
              <div>
                <p className="text-white font-medium font-['Outfit'] text-sm tracking-wide">EmCaLu AI Agency</p>
                <p className="text-white/40 text-xs font-light font-['Outfit'] tracking-widest uppercase mt-1">Strategic Partner for CEE Markets</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
